import React, { useEffect, useState, useRef, useCallback } from "react";
import RequisitionType from "../../../Services/Requisition/RequisitionTypeService";

const UseMedicationAutoComplete = (inputSearchRef: any, ArrayReqId: any, fieldConfiguration: any) => {
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [debouncedValue, setDebouncedValue] = useState<string>("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState("");
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [focusOut, setFocusOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  /** ==============================================
   *   CUSTOM DEBOUNCE – Debounce searchedValue
   *  ============================================== */
  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(searchedValue);
    }, 500);

    // Cleanup on unmount or when searchedValue changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchedValue]);

  const getDrugAllergies = useCallback((value: string) => {
    setLoading(true);
    let data = {};
    if (ArrayReqId === 11) {
      const template = fieldConfiguration?.parameters || "";
      const parameters = template
        .replace("drugName=|", `drugName=${encodeURIComponent(value)}`)
        .replace(/\|/g, ""); // remove remaining pipe chars

      data = {
        endPoint: fieldConfiguration?.endPoint,
        parameters,
      };
    }
    // TOX (ArrayReqId 3): when the field configuration has an endPoint, e.g.
    // { "endPoint": "/api/ToxCompendium/AllMedication/{drugName}/Lookup", "parameters": "type=SmartToxApi" },
    // only the medications configured in the TOX Medication table are searched; otherwise it works as before.
    const useToxConfiguration = ArrayReqId === 3 && !!fieldConfiguration?.endPoint;
    if (useToxConfiguration) {
      data = {
        endPoint: fieldConfiguration.endPoint.replace("{drugName}", encodeURIComponent(value)),
        parameters: fieldConfiguration?.parameters || "",
      };
    }
    // If ArrayReqId is 11 (or TOX is configured), use GetDrugBankData, otherwise use GetToxMedication[]
    const apiCall = ArrayReqId === 11 || useToxConfiguration
      ? RequisitionType.GetDrugBankData(data)
      : RequisitionType.GetToxMedication(value);

    apiCall
      .then((res: any) => {
        setSuggestions(res.data.data || []);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [ArrayReqId, fieldConfiguration?.endPoint, fieldConfiguration?.parameters]);

  /** ==============================================
   *   API CALL – Only triggers after debounce AND min 3 chars
   *  ============================================== */
  useEffect(() => {
    const trimmedValue = typeof debouncedValue === 'string' ? debouncedValue.trim() : '';
    if (trimmedValue.length >= 3) {
      getDrugAllergies(trimmedValue);
    } else {
      setSuggestions([]); // Clear suggestions if less than 3 characters
    }
  }, [debouncedValue, getDrugAllergies]);

  /** =====================
   *   HANDLE CHANGE
   *  =====================*/
  const handleChange = (event: { target: { value: string } }): void => {
    const value = event.target.value.trim();
    setTouched(true);

    // If empty → reset everything
    if (value === "") {
      setSearchedValue("");
      setSuggestions([]);
      setSelectedSuggestion("");
      setActiveSuggestion(0);
      return;
    }

    // If 1–2 characters → update input but don't search
    if (value.length < 3) {
      setSearchedValue(value);
      setSuggestions([]);
      return;
    }

    // If 3+ characters → allow search to run (debounced)
    setSearchedValue(value);
  };

  /** =====================
   *   HANDLE KEYDOWN
   *  =====================*/
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "ArrowDown" && activeSuggestion < suggestions.length) {
      setActiveSuggestion(activeSuggestion + 1);
    } else if (event.key === "ArrowUp" && activeSuggestion > 1) {
      setActiveSuggestion(activeSuggestion - 1);
    } else if (event.key === "Enter" && suggestions.length > 0) {
      const selected = suggestions[activeSuggestion - 1];
      if (!selected) return;

      const name = selected.name?.common || "";
      setSearchedValue(name);
      setSelectedSuggestion(name);
      setSuggestions([]);
      setActiveSuggestion(0);
    }
  };

  /** =====================
   *   HANDLE CLICK
   *  =====================*/
  const handleClick = (drug: any) => {
    setSuggestions([]);
    setSelectedSuggestion(drug);
    setSearchedValue(drug);
    setActiveSuggestion(0);
    setTouched(false);
  };

  return {
    setTouched,
    setActiveSuggestion,
    setSelectedSuggestion,
    searchedValue,
    setSearchedValue,
    setSuggestions,
    suggestions,
    selectedSuggestion,
    activeSuggestion,
    loading,
    touched,
    handleChange,
    handleKeyDown,
    handleClick,
    focusOut,
  };
};

export default UseMedicationAutoComplete;
